import React, {useRef, useEffect, useLayoutEffect} from "react";
import "./Textarea.css";
import "../ContentEditable/FieldColor.css";
import cn from "classnames";
import {useSetting} from "../../contexts/setting";
import {useTextState} from "../../hooks/useTextState";
import {useFieldColor} from "../../hooks/useFieldColor";
import {applyTypograf, revertTypograf, ensureProductNameNbsp} from "../../libs/typograf";
import {useReleaseSocialMediaFormatting} from "../../hooks/useReleaseSocialMediaFormatting";
import {isRichEditableText} from "../../libs/contentEditableText";
import ContentEditable from "../ContentEditable/ContentEditable";

/**
 * `normalize` — an optional per-slot rule for what may be stored (e.g. the
 * Modul face's lowercase-only, libs/modulCase.ts). Applied to every change,
 * so a paste is folded the same way a keystroke is.
 */
export const PlainTextarea = ({text = "", id, colorId = id, inline = false, normalize = undefined}) => {
  const {fontOption, isTypografEnabled} = useSetting();
  const labelRef = useRef(null);
  const inputRef = useRef(null);
  const isComposingRef = useRef(false);

  const {type, currentText, onTextChange, fontSizeClass, isPlaceholder, isLocked} = useTextState(
    id,
    text,
    labelRef
  );
  const {color: fieldColor} = useFieldColor(type, colorId);
  const fieldStyle = fieldColor ? {"--coverbaker-field-color": fieldColor} : undefined;

  const handleBlur = () => {
    // Mobile browsers can fire blur during active IME composition — skip to
    // avoid mangling in-flight kana/pinyin.
    if (isComposingRef.current || isPlaceholder || isLocked) return;
    let normalized = currentText;
    if (isTypografEnabled) {
      normalized = applyTypograf(currentText, fontOption);
    } else {
      // Product name nbsp insertion still applies even without typograf
      normalized = ensureProductNameNbsp(currentText);
    }
    if (normalized !== currentText) {
      onTextChange(normalized);
    }
  };

  // Instant re-normalization on Typograf toggle transitions: apply on OFF→ON,
  // strip typograf artifacts on ON→OFF. Keyed on isTypografEnabled only —
  // running on every currentText change would fight live typing.
  const wasTypografEnabledRef = useRef(isTypografEnabled);
  useEffect(() => {
    const wasEnabled = wasTypografEnabledRef.current;
    wasTypografEnabledRef.current = isTypografEnabled;
    if (wasEnabled === isTypografEnabled || isPlaceholder || isLocked) return;
    const next = isTypografEnabled
      ? applyTypograf(currentText, fontOption)
      : revertTypograf(currentText);
    if (next !== currentText) {
      onTextChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: trigger only on toggle transition
  }, [isTypografEnabled]);

  // Sync dataset.value for the auto-sizing label trick
  useEffect(() => {
    if (labelRef.current) {
      labelRef.current.dataset.value = currentText;
    }
  }, [currentText]);

  useLayoutEffect(() => {
    if (!labelRef.current || !inputRef.current) return;
    const lineHeight = window.getComputedStyle(inputRef.current).lineHeight;
    labelRef.current.style.setProperty("--input-sizer-line-height", lineHeight);
  });

  // If badge text is empty, hide product-name separator in the same header.
  useEffect(() => {
    const badge = labelRef.current?.closest(".badge");
    if (!badge) return;
    const header = badge.closest(".template-header");
    if (!header) return;
    const hasText = currentText.trim().length > 0;
    header.classList.toggle("no-badge-text", !hasText);
  }, [currentText]);

  const handleChange = (e) => {
    const newValue = normalize ? normalize(e.target.value) : e.target.value;
    onTextChange(newValue);
    if (labelRef.current) {
      labelRef.current.dataset.value = newValue;
    }
  };

  // Force browser to re-evaluate text shaping (OpenType calt ligatures) on the
  // actively-typed input.  React skips re-setting input.value when the DOM already
  // matches the prop, so contextual alternates built incrementally by typing
  // (e.g. "TeamCity") never get the full-string reshaping pass that other inputs
  // receive when their value is set from state.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input || document.activeElement !== input || isComposingRef.current) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;

    // Clear then re-set to force the browser to treat it as a new value
    input.value = "";
    input.value = currentText;

    input.setSelectionRange(start, end);
  }, [currentText]);

  const textareaClass = cn("textarea", `font-${fontOption}`, fontSizeClass);
  const finalTextareaClass = cn(textareaClass, {"placeholder-text": isPlaceholder});
  const labelClass = cn("input-sizer", fontSizeClass);

  return (
    <div className="textarea-wrap" data-field-id={id}>
      <label className={labelClass} ref={labelRef}>
        {inline ? (
          <input
            style={fieldStyle}
            data-field-color={fieldColor}
            ref={inputRef}
            type="text"
            size="1"
            className={finalTextareaClass}
            onChange={handleChange}
            onBlur={handleBlur}
            onCompositionStart={() => { isComposingRef.current = true; }}
            onCompositionEnd={() => { isComposingRef.current = false; }}
            value={currentText}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            spellCheck="false"
          />
        ) : (
          <textarea
            style={fieldStyle}
            data-field-color={fieldColor}
            ref={inputRef}
            className={finalTextareaClass}
            onChange={handleChange}
            onBlur={handleBlur}
            onCompositionStart={() => { isComposingRef.current = true; }}
            onCompositionEnd={() => { isComposingRef.current = false; }}
            value={currentText}
            rows="1"
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            spellCheck="false"
          />
        )}
      </label>
    </div>
  );
};

const FormattableTextarea = ({text = "", ...props}) => {
  const useRichEditor = useReleaseSocialMediaFormatting() || isRichEditableText(text);
  return useRichEditor ? <ContentEditable {...props} text={text} plain /> : <PlainTextarea {...props} text={text} />;
};

export default FormattableTextarea;
